"use client";

import { SolarMagniferLinear } from "@/components/icons/Solar";
import { IconButton } from "@/components/ui/IconButton";
import { Input } from "@/components/ui/Input";
import { useGlobal } from "@/features/global/GlobalContext";
import { Form, useGeo } from "@/i18n/routing";
import { formatPrice } from "@/lib/utils";

import { Translate } from "@/components/Translate";
import { AssetBlok, sizes } from "@/components/bloks/Asset";
import { ChevronRight } from "@/components/icons/Chevron";
import { Divider } from "@/components/ui/Divider";
import {
	PopoverClose,
	PopoverContent,
	PopoverRoot,
	PopoverTrigger,
	usePopover,
} from "@/components/ui/Popover";
import { withSearchParams } from "@/components/withSearchParams";
import type { StoriesRoute } from "@/data/routes/stories.route";
import { getProductLink } from "@/features/products/ProductCard/util";
import { Link } from "@/i18n/Link";
import { useLang, useTranslations } from "@/i18n/client";
import type { ProductCard } from "@/lib/centra";
import { formatProductCard } from "@/lib/centra/formatters";
import { depictFormatter } from "@/lib/depict/formatters";
import { depict } from "@/lib/depict/sdk";
import type { DepictDisplay } from "@/lib/depict/types";
import { localeToBCP47 } from "@/lib/depict/utils";
import { sbLink } from "@/lib/storyblok/link";
import { getProductName } from "@/lib/utils";
import { useCentraClient, useToken } from "@frend-digital/centra/client";
import type { DepictOptions } from "@frend-digital/depict/server";
import { sendGTMEvent } from "@frend-digital/events/gtm";
import { clsx } from "@frend-digital/ui";
import { useDebouncedValue } from "@frend-digital/ui/hooks";
import { addTrailingSlash } from "@frend-digital/utils";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import Image from "next/image";
import type React from "react";
import {
	type FormEvent,
	Suspense,
	startTransition,
	useCallback,
	useEffect,
	useId,
	useRef,
	useState,
} from "react";
import { rpc } from "#rpc";
import styles from "./Search.module.css";

export type SearchProps = {};

const UnsuspendedSearch = withSearchParams<{ className?: string }>(
	({ className, searchParams }) => {
		const { data } = useGlobal();
		const link = data.pages.search?.href;

		if (!link) {
			console.error("No link found for search");
			return null;
		}

		return (
			<PopoverRoot>
				<SearchPopoverContent link={link} searchParams={searchParams} className={className} />
			</PopoverRoot>
		);
	},
);

export const SearchForm = () => {
	const { data } = useGlobal();
	const link = data.pages.search?.href;

	if (!link) {
		console.error("No link found for search");
		return null;
	}

	return <ScrollableSearchForm link={link} />;
};

const ScrollableSearchForm = ({ link }: { link: string }) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const [isMobile, setIsMobile] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);
	const formRef = useRef<HTMLFormElement>(null);
	const formId = useId();

	const handleResize = useCallback(() => {
		const mobile = window.innerWidth <= 768;
		setIsMobile(mobile);
		if (!mobile) {
			setIsExpanded(false);
		}
	}, []);

	useEffect(() => {
		let timeoutId: NodeJS.Timeout;
		const debouncedResize = () => {
			clearTimeout(timeoutId);
			timeoutId = setTimeout(handleResize, 150);
		};
		handleResize();
		window.addEventListener("resize", debouncedResize);
		return () => {
			clearTimeout(timeoutId);
			window.removeEventListener("resize", debouncedResize);
		};
	}, [handleResize]);

	useEffect(() => {
		if (isExpanded && inputRef.current) {
			// Use requestAnimationFrame to ensure DOM is ready
			requestAnimationFrame(() => {
				inputRef.current?.focus();
			});
		}
	}, [isExpanded]);

	// Outside click detection for expanded form only
	useEffect(() => {
		if (!isExpanded) return;

		const handleOutsideClick = (event: MouseEvent) => {
			const target = event.target as Element;

			if (formRef.current?.contains(target)) {
				return;
			}

			setIsExpanded(false);
		};

		setTimeout(() => {
			document.addEventListener("mousedown", handleOutsideClick);
		}, 0);

		return () => document.removeEventListener("mousedown", handleOutsideClick);
	}, [isExpanded]);

	const handleExpand = () => {
		setIsExpanded(true);
	};

	const handleCollapse = () => {
		setIsExpanded(false);
	};

	if (isMobile) {
		return (
			<>
				<CollapsedSearchButton onClick={handleExpand} expanded={isExpanded} formId={formId} />
				{isExpanded && (
					<InternalSearchForm
						link={link}
						ref={inputRef}
						onSubmit={handleCollapse}
						className={styles.expandedSearch}
						id={formId}
						formRef={formRef}
					/>
				)}
			</>
		);
	}

	return <InternalSearchForm link={link} ref={inputRef} />;
};

const CollapsedSearchButton = ({
	onClick,
	expanded,
	formId,
}: { onClick: () => void; expanded: boolean; formId: string }) => {
	const t = useTranslations();

	return (
		<button
			type="button"
			className={styles.collapsedButton}
			onClick={onClick}
			aria-label={t("search")}
			aria-expanded={expanded}
			aria-controls={formId}
		>
			<SolarMagniferLinear />
			<span className={styles.title}>
				<Translate>search</Translate>
			</span>
		</button>
	);
};

export const InternalSearchForm = withSearchParams<{
	onSubmit?: (e: FormEvent) => void;
	ref?: React.Ref<HTMLInputElement>;
	link: string;
	setSearch?: React.Dispatch<React.SetStateAction<string>>;
	popover?: boolean;
	isExpanded?: boolean;

	className?: string;
	id?: string;
	formRef?: React.Ref<HTMLFormElement>;
}>(
	({
		onSubmit,
		ref,
		link,
		setSearch,
		popover,
		isExpanded,
		className,
		searchParams,
		id,
		formRef,
	}) => {
		const t = useTranslations();
		const CloseComponent = popover ? PopoverClose : "button";
		return (
			<Form
				id={id}
				ref={formRef}
				onSubmit={(e) => {
					const data = new FormData(e.target as HTMLFormElement);
					if (!data.get("q")) {
						e.preventDefault();
					}
					onSubmit?.(e);
				}}
				action={addTrailingSlash(link)}
				className={clsx(styles.root, isExpanded && styles.expanded, className)}
			>
				<div className={styles.inputWrapper}>
					<Input
						key={searchParams.get("q")}
						ref={ref}
						onChange={(e) => {
							startTransition(() => {
								setSearch?.(e.target.value);
							});
						}}
						defaultValue={searchParams.get("q") || undefined}
						name="q"
						placeholder={t("Search...")}
						className={styles.input}
					/>
					<CloseComponent type="submit" className={styles.searchButton}>
						<SolarMagniferLinear />
					</CloseComponent>
				</div>
			</Form>
		);
	},
);

const SearchPopoverContent = ({
	link,
	searchParams,
	className,
}: { link: string; searchParams: URLSearchParams; className?: string }) => {
	const t = useTranslations();
	const { popoverId, setOpen, open } = usePopover();
	const inputRef = useRef<HTMLInputElement>(null);
	const [search, setSearch] = useState("");

	useEffect(() => {
		const popover = document.getElementById(popoverId);

		if (popover) {
			const handleShow = () => {
				inputRef.current?.focus();
			};

			popover.addEventListener("toggle", handleShow);
			return () => popover.removeEventListener("toggle", handleShow);
		}
	}, [popoverId]);

	return (
		<>
			<PopoverTrigger asChild>
				<IconButton type="button" aria-label={t("Search")} aria-expanded={open}>
					<SolarMagniferLinear />
				</IconButton>
			</PopoverTrigger>

			<PopoverContent className={clsx(styles.popover, className)}>
				<InternalSearchForm
					popover
					link={link}
					ref={inputRef}
					onSubmit={(e) => {
						const raw = new FormData(e.target as HTMLFormElement).get("q");
						const searchTerm = typeof raw === "string" ? raw : "";
						if (searchTerm) {
							sendGTMEvent({ event: "view_search_results", search_term: searchTerm });
						}
						(document.getElementById(popoverId) as HTMLFormElement)?.hidePopover();
					}}
					setSearch={setSearch}
					id={`search-form-${popoverId}`}
				/>

				<Divider />

				<SearchResults
					onClose={() => {
						if (search) {
							sendGTMEvent({ event: "view_search_results", search_term: search });
						}
						setOpen(false);
						(document.getElementById(popoverId) as HTMLFormElement)?.hidePopover();
					}}
					q={search}
				/>
			</PopoverContent>
		</>
	);
};

const SearchResults = ({ q, onClose }: { q: string; onClose: () => void }) => {
	const client = useCentraClient();
	const userData = useGeo();
	const token = useToken();
	const debounced = useDebouncedValue(q, 150);
	const language = useLang();
	const { country } = userData;

	const depictOptions: DepictOptions = {
		market: userData?.country.toLowerCase()!,
		session_id: token!,
		locale: localeToBCP47(language),
	};

	const { data: products } = useQuery({
		queryKey: ["search", debounced],
		queryFn: async () => {
			if (!q) return [];
			const depictResult = await depict.search(q, depictOptions);

			if (depictResult.err) return [];
			const sortedProductIds = depictResult.val.displays
				.map((dis) => {
					const formattedDisplay = depictFormatter(dis as unknown as DepictDisplay);
					return formattedDisplay.displayItemId;
				})
				.sort();

			const { data, error } = await client.POST("/products", {
				body: {
					relatedProducts: false,
					limit: 4,
					products: sortedProductIds,
				},
			});
			if (error) return [];

			return data.products?.map(formatProductCard) || [];
		},
		placeholderData: keepPreviousData,
	});

	const { data: stories } = useQuery({
		queryKey: ["sb-search", debounced, country],
		queryFn: async () => {
			if (!debounced) return [];
			return rpc.stories.search({ q: debounced, limit: 4, language, country });
		},
		placeholderData: keepPreviousData,
		refetchOnMount: false,
	});

	const { data: categories } = useQuery({
		queryKey: ["category-search", debounced, language, country],
		queryFn: async () => {
			if (!debounced) return [];
			return rpc.stories.searchCategories({ q: debounced, language, limit: 4, country });
		},
		placeholderData: keepPreviousData,
		refetchOnMount: false,
	});

	if (!products?.length && !stories?.length && !categories?.length) {
		return null;
	}

	return (
		<section>
			{!!products?.length && (
				<div className={styles.results}>
					<p className={styles.title}>
						<Translate>Products</Translate>
					</p>
					{products.map((product) => {
						return (
							<SearchProductCard onClose={onClose} key={product.displayItemId} product={product} />
						);
					})}
					<Link href={`/search?q=${encodeURIComponent(debounced)}`} onClick={onClose}>
						<Translate>View all products</Translate>
					</Link>
				</div>
			)}

			{!!categories?.length && (
				<div className={styles.results}>
					<p className={styles.title}>
						<Translate>Categories</Translate>
					</p>
					{categories.map((category) => {
						return <SearchCategoryCard onClose={onClose} key={category.slug} category={category} />;
					})}
				</div>
			)}

			{!!stories?.length && (
				<div className={styles.results}>
					<p className={styles.title}>
						<Translate>Articles</Translate>
					</p>

					{stories.map((story) => {
						return <SearchStoryCard onClose={onClose} key={story.full_slug} story={story} />;
					})}
				</div>
			)}
		</section>
	);
};

const SearchCategoryCard = ({
	category,
	onClose,
}: {
	category: Awaited<ReturnType<typeof rpc.stories.links>>[number];
	onClose: () => void;
}) => {
	const link = sbLink(category.slug);

	if (!link) return null;

	const getParentCategory = (slug: string) => {
		const segments = slug.split("/");

		return segments.length > 2 ? segments[1] : null;
	};

	const parentCategory = getParentCategory(category.slug);

	return (
		<Link
			onClick={onClose}
			href={link.href}
			className={clsx(styles.productCard, styles.fullWidth, "unstyled")}
		>
			<div className={styles.info}>
				<dt className={styles.title}>
					{category.name}
					{parentCategory && <span className={styles.parentCategory}> {parentCategory}</span>}
				</dt>
			</div>
			<div className={styles.info}>
				<ChevronRight />
			</div>
		</Link>
	);
};

const SearchProductCard = ({ product, onClose }: { product: ProductCard; onClose: () => void }) => {
	const media = product.media[0];
	return (
		<Link
			onClick={onClose}
			href={getProductLink(undefined, product)}
			className={clsx(styles.productCard, "unstyled")}
		>
			<div className={styles.image}>
				{media && <Image sizes={sizes(50, 25)} src={media.href} alt="" fill />}
			</div>
			<dl className={styles.info}>
				<dt className={styles.title}>{getProductName(product)}</dt>
				<dd className={styles.price}>{formatPrice(product.price)}</dd>
				<dd className={styles.data}>{product.variantName}</dd>
				<dd className={clsx(styles.sku, styles.data)}>SKU: {product.sku}</dd>
			</dl>

			<div className={styles.info}>
				<ChevronRight />
			</div>
		</Link>
	);
};

const SearchStoryCard = ({
	story,
	onClose,
}: { story: StoriesRoute["search"][number]; onClose: () => void }) => {
	const link = sbLink(story.full_slug);
	const image = story.image;

	if (!link) return null;

	return (
		<Link
			onClick={onClose}
			href={link.href}
			className={clsx(styles.productCard, styles.reversed, "unstyled")}
		>
			<dl className={styles.info}>
				<dt className={styles.title}>{story.name}</dt>
			</dl>
			{image?.filename ? (
				<div className={styles.image}>
					{image?.filename && <AssetBlok source={image} sizes={sizes(50, 25)} />}
				</div>
			) : (
				<div />
			)}
			<div className={styles.info}>
				<ChevronRight />
			</div>
		</Link>
	);
};

export const Search = ({ className }: { className?: string }) => {
	return (
		<Suspense
			fallback={
				<form className={styles.root}>
					<IconButton>
						<SolarMagniferLinear />
					</IconButton>
				</form>
			}
		>
			<UnsuspendedSearch className={className} />
		</Suspense>
	);
};
